/** Pure settlement calculations shared by compensation screens. */
import {
  moneyCents,
  parseMoneyInput,
  payoutSettlement,
  settlementIsOutstanding,
  settlementPaymentStatus
} from '../../../supabase/functions/_shared/payout-settlement';

export { payoutSettlement } from '../../../supabase/functions/_shared/payout-settlement';

export function validatePaymentAmount(input: string): { valid: boolean; amount: number; error?: string } {
  const amount = parseMoneyInput(input);
  if (amount === null || amount <= 0) {
    return { valid: false, amount: 0, error: 'Enter an amount greater than zero with at most two decimal places' };
  }
  return { valid: true, amount };
}

export function hasInvalidBulkPaymentAmounts(amounts: string[]): boolean {
  return amounts.some(amount => !validatePaymentAmount(amount).valid);
}

export function accumulatePayment(existingPaid: number, enteredAmount: number): number {
  return (moneyCents(existingPaid) + moneyCents(enteredAmount)) / 100;
}

/** Matches refresh_payout_payment_totals, including the existing one-cent tolerance. */
export function determinePaymentStatus(paymentAmount: number, netAmount: number, recapturedAmount = 0): 'paid' | 'approved' {
  return settlementPaymentStatus(payoutSettlement(netAmount, paymentAmount, recapturedAmount));
}

/** Signed balances preserve LO credits when grouped. */
export function computeBalance(netPayout: number, paymentAmount: number, recapturedAmount = 0): number {
  return payoutSettlement(netPayout, paymentAmount, recapturedAmount).remaining;
}

export function isPayoutOutstanding(netPayout: number, paymentAmount: number, recapturedAmount = 0, payoutType?: string): boolean {
  if (payoutType === 'Volume Bonus' || payoutType === 'volume_bonus') {
    return payoutSettlement(netPayout, paymentAmount, recapturedAmount).remaining > 0;
  }
  return settlementIsOutstanding(payoutSettlement(netPayout, paymentAmount, recapturedAmount));
}

/** Preserve legacy cash-edit defaults; a recapture-settled row has nothing payable. */
export function computeDialogDefault(netPayout: number, paymentAmount: number, recapturedAmount = 0, payoutType?: string): number {
  const balance = computeBalance(netPayout, paymentAmount, recapturedAmount);
  if (payoutType === 'Volume Bonus' || payoutType === 'volume_bonus') {
    return Math.max(0, balance);
  }
  return balance > 0.01 ? balance : recapturedAmount > 0 ? 0 : netPayout;
}

export function computeBaseComp(loCommissionBase: number, grossPayout: number, reimbursements: number): number {
  return loCommissionBase > 0 ? loCommissionBase : grossPayout - reimbursements;
}

export interface PayoutBalanceInput {
  loId: string;
  loName: string;
  netPayout: number;
  paymentAmount: number;
  recapturedAmount?: number;
  type?: string;
  status: 'pending' | 'approved' | 'paid';
}

export interface LoBalance {
  loId: string;
  loName: string;
  totalOwed: number;
  totalPaid: number;
  totalRecaptured: number;
  balance: number;
  payoutCount: number;
  paidCount: number;
  unpaidCount: number;
}

export function computeLoBalances(payouts: PayoutBalanceInput[]): LoBalance[] {
  const byLO = new Map<string, LoBalance>();
  for (const payout of payouts) {
    const lo = byLO.get(payout.loId) ?? {
      loId: payout.loId, loName: payout.loName,
      totalOwed: 0, totalPaid: 0, totalRecaptured: 0, balance: 0,
      payoutCount: 0, paidCount: 0, unpaidCount: 0
    };
    const settlement = payoutSettlement(payout.netPayout || 0, payout.paymentAmount || 0, payout.recapturedAmount || 0);
    lo.totalOwed = accumulatePayment(lo.totalOwed, settlement.net);
    lo.totalPaid = accumulatePayment(lo.totalPaid, settlement.cash);
    lo.totalRecaptured = accumulatePayment(lo.totalRecaptured, settlement.recaptured);
    lo.balance = computeBalance(lo.totalOwed, lo.totalPaid, lo.totalRecaptured);
    lo.payoutCount++;
    const outstanding = isPayoutOutstanding(settlement.net, settlement.cash, settlement.recaptured, payout.type);
    if (!outstanding) {
      lo.paidCount++;
    } else {
      lo.unpaidCount++;
    }
    byLO.set(payout.loId, lo);
  }
  return [...byLO.values()].sort((a, b) => a.loName.localeCompare(b.loName));
}
